import { toRef, toRefs, ref, computed, reactive, watch, unref, Ref } from '@vue/composition-api';
import { useMutation, useQuery } from 'vue-query';
import useVuelidate from '@vuelidate/core';
import {
  AtoneAuthenticatePostResponseSexDivisionEnum,
  AtoneTracksPostRequestAuthenticationTypeEnum,
} from '@netprotections/berlin-bff';
import useErrors from '@composables/useErrors';
import {
  maxLength,
  regexp,
  REG_PHONE_ONLY_NUMBER_STYLE,
  REG_PHONE_ONLY_NUMBER_STYLE_ERR_MSG,
  required,
  getErrors,
} from '@berlin-front/common/composables/validator';
import useFingerprint from '@berlin-front/common/composables/useFingerprint';
import useOrAll from '@berlin-front/common/composables/useOrAll';
import useFakeProgress from '@berlin-front/common/composables/useFakeProgress';
import { useSessionId } from '@berlin-front/common/composables/useSessionId';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import { event, set } from 'vue-gtag';
import { MemberRequestContext } from '@views/member/MemberRequestContext';
import { getRefDeep, notNil, windowOpenNewTab } from '@berlin-front/common/utils';
import { useContext } from '@berlin-front/common/composables/context';
import { BerlinBffContext } from '@services/BerlinBff';
import { ApiCacheContext } from '@services/ApiCacheContext';
import { ROUTE_NAME } from '@/router/route-name';
import useIsLegacy from '@composables/useIsLegacy';
import { ModalBridgeContext } from '@services/ModalBridgeContext';
import { AuthLoginAtoneMemberContextType } from './context';

/**
 * ログインAPI（AtoneAuthenticateController_authenticatePost）
 */
const useAuthenticate = (): {
  isSuccess: Ref<boolean>;
  isError: Ref<boolean>;
  authenticate: (state: { atoneId: string; password: string }) => void;
} => {
  const [isLegacy] = useIsLegacy();
  const sessionId = useSessionId();
  const { atoneAuthModalParameter, setAtoneAuthenticatePostResult, setNpTokenAndSignature } =
    useContext(ApiCacheContext);
  const { authenticate } = useContext(BerlinBffContext);
  const globals = useContext(ModalBridgeContext);
  const { isSuccess, isError, mutate } = useMutation(
    'auth.login',
    (state: { atoneId: string; password: string }) =>
      authenticate.atoneAuthenticateControllerAuthenticatePost({
        signInId: state.atoneId,
        password: state.password,
        shopPublicKey: atoneAuthModalParameter.value.pubKey,
        userNo: atoneAuthModalParameter.value.payment.userNo,
        sessionId,
        isOldParameter: isLegacy.value,
        requestId: globals.requestId,
      }),
    {
      retry: false,
      onSuccess: ({ data: atoneAuthenticatePostResult }) => {
        setAtoneAuthenticatePostResult(atoneAuthenticatePostResult);
        // TODO: スキーマ修正してrequiredにする。
        if (atoneAuthenticatePostResult.token) {
          setNpTokenAndSignature(atoneAuthenticatePostResult.token, atoneAuthenticatePostResult.signature);
        }
        window.newrelic.addPageAction('login_success', {
          member_id: notNil(atoneAuthenticatePostResult.memberCode),
          shop_id: notNil(atoneAuthenticatePostResult.shopId),
          shop_name: notNil(atoneAuthenticatePostResult.shopName),
          gaid: notNil(atoneAuthenticatePostResult.hashId),
        });

        let gender: string | null;
        // eslint-disable-next-line no-underscore-dangle
        if (atoneAuthenticatePostResult.sexDivision === AtoneAuthenticatePostResponseSexDivisionEnum._1) {
          gender = '男性';
          // eslint-disable-next-line no-underscore-dangle
        } else if (atoneAuthenticatePostResult.sexDivision === AtoneAuthenticatePostResponseSexDivisionEnum._2) {
          gender = '女性';
        } else {
          gender = '不明';
        }

        set({
          user_id: notNil(atoneAuthenticatePostResult.memberCode),
          user_properties: {
            member_id: notNil(atoneAuthenticatePostResult.memberCode),
            gender,
            age: atoneAuthenticatePostResult.age,
            shop_id: notNil(atoneAuthenticatePostResult.shopId),
            // shop_name: notNil(atoneAuthenticatePostResult.shopName),
            guest: 'no',
          },
        });

        event('login', {
          method: 'Atone',
        });
      },
    },
  );
  return { isSuccess, isError, authenticate: mutate };
};

/**
 * フィンガープリント登録（AtoneTracksController_trackTokenPost）
 */
const useRegisterFingerprint = ({
  enabled,
}: {
  enabled: Ref<boolean>;
}): {
  isSuccess: Ref<boolean>;
  isError: Ref<boolean>;
} => {
  const { atoneAuthModalParameter, tokens, setAtoneTracksPostResult } = useContext(ApiCacheContext);
  const { tracks } = useContext(BerlinBffContext);
  const fingerprint = useFingerprint();
  const { isSuccess, isError } = useQuery(
    'auth.atoneTrackTokenPost',
    () =>
      tracks.atoneTracksControllerTrackTokenPost(
        getRefDeep(tokens, 'npToken', ''),
        atoneAuthModalParameter.value.pubKey,
        {
          authenticationType: AtoneTracksPostRequestAuthenticationTypeEnum.NUMBER_1,
          fingerPrint: fingerprint.value,
        },
      ),
    {
      enabled: computed(() => fingerprint.resolved && unref(enabled)),
      onSuccess: ({ data }) => {
        setAtoneTracksPostResult(data);
      },
    },
  );
  return { isSuccess, isError };
};

/**
 * フィンガープリント検証（AtoneTracksController_trackTokenTrust）
 */
const useVerifyFingerprint = ({
  enabled,
}: {
  enabled: Ref<boolean>;
}): {
  isSuccess: Ref<boolean>;
  isError: Ref<boolean>;
  isTrusted: Ref<boolean>;
} => {
  const { atoneAuthModalParameter, tokens, atoneTracksPostResult, setAtoneTracksTrustResult, atoneTracksTrustResult } =
    useContext(ApiCacheContext);
  const { tracks } = useContext(BerlinBffContext);
  const { isSuccess, isError } = useQuery(
    'atone.TrackTokenTrust',
    () =>
      tracks.atoneTracksControllerTrackTokenTrust(
        getRefDeep(atoneTracksPostResult, 'trackToken'),
        getRefDeep(tokens, 'npToken'),
        atoneAuthModalParameter.value.pubKey,
      ),
    {
      enabled: computed(() => unref(enabled)),
      onSuccess: ({ data }) => {
        setAtoneTracksTrustResult(data);
      },
    },
  );
  const isTrusted = computed(() => (unref(isSuccess) ? getRefDeep(atoneTracksTrustResult, 'trustedFlag') : false));

  return { isSuccess, isError, isTrusted };
};

/**
 * SMS送信API（AtoneSmsController_smsNotifyPost）
 */
const useSendSmsCode = ({
  enabled,
  phoneNumber,
}: {
  enabled: Ref<boolean>;
  phoneNumber: string;
}): {
  isSuccess: Ref<boolean>;
  isError: Ref<boolean>;
} => {
  const { atoneAuthModalParameter, tokens, atoneTracksPostResult, setAtoneSmsNotifyPostResult } =
    useContext(ApiCacheContext);
  const { sms } = useContext(BerlinBffContext);
  const { isSuccess, isError } = useQuery(
    'atone.SmsNotifyPost',
    () =>
      sms.atoneSmsControllerSmsNotifyPost(getRefDeep(tokens, 'npToken'), atoneAuthModalParameter.value.pubKey, {
        trackToken: getRefDeep(atoneTracksPostResult, 'trackToken'),
        resend: false,
      }),
    {
      enabled,
      onSuccess: ({ data: atoneSmsNotifyPostResult }) => {
        setAtoneSmsNotifyPostResult(atoneSmsNotifyPostResult);
      },
      onError: () => {
        // ログイン時の電話番号を仕掛ける。
        setAtoneSmsNotifyPostResult({
          phoneNumber,
        });
      },
    },
  );
  return { isSuccess, isError };
};

export default {
  useLogin: (): ReturnType<AuthLoginAtoneMemberContextType['useLogin']> => {
    const router = useRouter();
    const { registerMemberFormState, normalize } = useContext(MemberRequestContext);
    const { errors, clearAll } = useErrors({
      fieldMapper: {},
      defaultFieldName: 'flashMessages',
    });

    // フォームの入力内容
    const state = reactive({
      atoneId: toRef(registerMemberFormState, 'phoneNumber'),
      password: toRef(registerMemberFormState, 'password'),
    });

    // メッセージ
    const messages = computed(() => unref(errors)?.flashMessages ?? []);

    const isLoadingApi = ref(false);

    // ログインAPI。「submit」された時に呼び出す。
    const { isSuccess: isAuthenticateSucceeded, isError: isAuthenticateError, authenticate } = useAuthenticate();

    // フィンガープリント登録。ログインAPIが「成功」した場合に呼び出す。
    const { isSuccess: isRegisterFingerprintSucceeded, isError: isRegisterFingerprintError } = useRegisterFingerprint({
      enabled: isAuthenticateSucceeded,
    });

    // フィンガープリント検証。フィンガープリント登録が「成功」した場合に呼び出す。
    const {
      isSuccess: isVerifyFingerprintSucceeded,
      isError: isVerifyFingerprintError,
      isTrusted,
    } = useVerifyFingerprint({
      enabled: isRegisterFingerprintSucceeded,
    });

    // SMS送信API。フィンガープリント検証が「無効」の場合に呼び出す。
    const { isSuccess: isSendSmsCodeSucceeded, isError: isSendSmsCodeError } = useSendSmsCode({
      enabled: computed(() => unref(isVerifyFingerprintSucceeded) && !unref(isTrusted)),
      phoneNumber: state.atoneId,
    });

    // エラーが発生したかどうか
    const isError = useOrAll(isAuthenticateError, isRegisterFingerprintError, isVerifyFingerprintError);
    // 成功したかどうか（フィンガープリント検証が「有効」の場合、もしくは成否問わずSMS送信が終了した場合）
    const isSuccess = useOrAll(isTrusted, isSendSmsCodeSucceeded, isSendSmsCodeError);
    // 終了したかどうか（成功、もしくはエラーになったら終了）
    const isEnded = useOrAll(isSuccess, isError);

    // 進捗。useMutationの状況に合わせてプログレスを進める。
    const {
      progress,
      completed: progressCompleted,
      isLoading: progressIsLoading,
    } = toRefs(useFakeProgress(5000, isLoadingApi, isEnded));

    // バリデーション
    const rules = computed(() => ({
      atoneId: {
        required,
        regexp: regexp(REG_PHONE_ONLY_NUMBER_STYLE, REG_PHONE_ONLY_NUMBER_STYLE_ERR_MSG),
      },
      password: {
        required,
        maxLength: maxLength(30),
      },
    }));
    const v$ = useVuelidate(rules, state);
    const getError = getErrors(v$);

    const isSubmitButtonDisabled = computed(
      () =>
        state.atoneId === '' ||
        state.password === '' ||
        unref(getError)('atoneId').$error ||
        unref(getError)('password').$error,
    );

    // submitした時の処理
    const onSubmit = async () => {
      // 以下の項目を確認した上でリクエストを行う。
      // - validationの結果が正常であること
      // - 実行中でないこと
      // - 実行後（正常系）でないこと
      if ((await v$.value.$validate()) && !unref(isLoadingApi) && !unref(isSuccess)) {
        clearAll();
        isLoadingApi.value = true;
        authenticate(state);
      }
    };

    // パスワード再発行のページを開く処理
    const openMyPageRemind = () => {
      windowOpenNewTab(process.env.VUE_APP_ATONE_PASSWORD_REMIND_URL);
    };

    // 「戻る」ボタン押下時に前の画面へ遷移する処理
    const goBack = async () => {
      await router.replace({ name: ROUTE_NAME.ATONEAUTH_INTRO });
    };

    // 終了時の処理
    watch(progressCompleted, (result) => {
      if (result && unref(isSuccess)) {
        // フィンガープリント検証が「有効」の場合は認証完了画面に遷移
        if (unref(isTrusted)) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          router.replace({ name: ROUTE_NAME.ATONEAUTH_COMPLETE });
          return;
        }
        // フィンガープリント検証が「無効」の場合はSMS認証画面に遷移
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.replace({ name: ROUTE_NAME.ATONEAUTH_ATONE_MEMBER_SMS_AUTH });
        return;
      }
      isLoadingApi.value = false;
    });

    return reactive({
      title: 'ログイン',
      state,

      // progress
      progress,
      progressIsLoading,
      messages,

      submitButton: {
        isDisabled: isSubmitButtonDisabled,
        onClick: onSubmit,
      },

      // action
      openMyPageRemind,
      showHelp: undefined,
      goBack,
      normalize,

      // validator
      v$,
      getError,

      // 利用規約
      TERMS_OF_USE_URL: process.env.VUE_APP_ATONE_TERMS_OF_USE_URL,
    });
  },
} satisfies AuthLoginAtoneMemberContextType;
